import type { ResourcePool } from "../types";

export const RESOURCE_KEYS = [
  "food",
  "production",
  "trade",
  "science",
  "culture",
] as const satisfies readonly (keyof ResourcePool)[];

export type ResourcePoolInput = Readonly<Partial<ResourcePool>>;

function readAmount(pool: ResourcePoolInput, key: keyof ResourcePool): number {
  const amount = pool[key] ?? 0;
  if (!Number.isSafeInteger(amount) || amount < 0) {
    throw new RangeError(`Resource '${key}' must be a non-negative safe integer.`);
  }
  return amount;
}

export function normalizeResourcePool(pool: ResourcePoolInput = {}): ResourcePool {
  return {
    food: readAmount(pool, "food"),
    production: readAmount(pool, "production"),
    trade: readAmount(pool, "trade"),
    science: readAmount(pool, "science"),
    culture: readAmount(pool, "culture"),
  };
}

export function addResources(left: ResourcePoolInput, right: ResourcePoolInput): ResourcePool {
  const first = normalizeResourcePool(left);
  const second = normalizeResourcePool(right);
  return normalizeResourcePool({
    food: first.food + second.food,
    production: first.production + second.production,
    trade: first.trade + second.trade,
    science: first.science + second.science,
    culture: first.culture + second.culture,
  });
}

/** Subtraction is component-wise and clamps at zero; it never converts resource types. */
export function subtractResources(
  available: ResourcePoolInput,
  required: ResourcePoolInput,
): ResourcePool {
  const supply = normalizeResourcePool(available);
  const cost = normalizeResourcePool(required);
  return {
    food: Math.max(0, supply.food - cost.food),
    production: Math.max(0, supply.production - cost.production),
    trade: Math.max(0, supply.trade - cost.trade),
    science: Math.max(0, supply.science - cost.science),
    culture: Math.max(0, supply.culture - cost.culture),
  };
}

export function canPayResources(
  available: ResourcePoolInput,
  required: ResourcePoolInput,
): boolean {
  const supply = normalizeResourcePool(available);
  const cost = normalizeResourcePool(required);
  return RESOURCE_KEYS.every((key) => supply[key] >= cost[key]);
}

/** Deduct an exact typed cost after proving every resource is available. */
export function payResources(
  available: ResourcePoolInput,
  required: ResourcePoolInput,
): ResourcePool {
  const pool = normalizeResourcePool(available);
  const cost = normalizeResourcePool(required);
  if (!RESOURCE_KEYS.every((key) => pool[key] >= cost[key])) {
    throw new RangeError("Insufficient exact resources for the required cost.");
  }

  return {
    food: pool.food - cost.food,
    production: pool.production - cost.production,
    trade: pool.trade - cost.trade,
    science: pool.science - cost.science,
    culture: pool.culture - cost.culture,
  };
}

export function getResourceShortfall(
  available: ResourcePoolInput,
  required: ResourcePoolInput,
): ResourcePool {
  const supply = normalizeResourcePool(available);
  const cost = normalizeResourcePool(required);
  return {
    food: Math.max(0, cost.food - supply.food),
    production: Math.max(0, cost.production - supply.production),
    trade: Math.max(0, cost.trade - supply.trade),
    science: Math.max(0, cost.science - supply.science),
    culture: Math.max(0, cost.culture - supply.culture),
  };
}

export function isZeroResourcePool(pool: ResourcePoolInput): boolean {
  const normalized = normalizeResourcePool(pool);
  return RESOURCE_KEYS.every((key) => normalized[key] === 0);
}
